import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActionResultClass, ResultClass } from '../model/search-result';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'projects/environments/environment';
import { DataService, IHttpCallBack } from './data.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { NGXLogger } from 'ngx-logger';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

@Injectable({
    providedIn: 'root'
})
export class AuthService extends DataService {
    SiteUrl: string;
    jwtHelper = new JwtHelperService();
    StartGetting: boolean;
    isExpired: boolean;
    datePipe: DatePipe;

    constructor(
      private httpClient: HttpClient,
      public logger: NGXLogger,
      private toastr: ToastrService) {
        super(httpClient, logger);
        this.SiteUrl = this.BaseUrl;
        this.StartGetting = false;
        this.datePipe = new DatePipe('en-US');
    }

    public GetSSOToken(body: any, successCallback: IHttpCallBack, failCallback: IHttpCallBack = null) {
      console.log('[AuthService][GetSSOToken]');
      const loginUrl = this.BaseUrl + 'Auth/SSOLogin';
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
          'Auth': "Basic " + btoa(`${body.ApplicationID};${body.AuthCode};${body.UserId};${body.Password};${body.WebUrl}`)
        })
      };
      this.httpClient.post<ActionResultClass>(loginUrl, null, httpOptions)
        .pipe(
            catchError(this.handleError<ActionResultClass>(`login error`))
        )
        .subscribe(res => {
            if (res && res.Status === 'Success') {
                console.log('[AuthService][GetSSOToken] Success');
                successCallback(res);
            } else {
                const msg = res ? res.Message : 'error';
                console.error(`[AuthService][GetSSOToken] res error: ${msg}`);
                if (failCallback) {
                    failCallback(res);
                }
            }
        }, err => {
          console.error(`[AuthService][GetSSOToken] error: ${err}`);
        });
    }

    public async SingleSignOn(SessionId: string) {
        console.log('[AuthService][SingleSignOn]');
        const authUrl = environment.APIBaseUrl + 'Login/SSO';
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Auth': SessionId
          })
        };
        const res = await this.httpClient.get<ResultClass>(authUrl, httpOptions).toPromise<ResultClass>();
        if (res && res.Success) {
          console.log('[AuthService][SingleSignOn] Success');
          const data = res.Result;
          localStorage.setItem('access_token', data.Token);
          localStorage.setItem('user', JSON.stringify(data));
        } else {
          console.error('[AuthService][SingleSignOn] res error', res);
          const msg = res ? res.Message : 'error';
          throw new Error(`${msg}`);
        }
    }

    public RefreshToken() {
      console.log('[AuthService][RefreshToken]');
      this.StartGetting = true;
      const tokenUrl = this.BaseUrl + 'Auth/RefreshToken';
      const body = JSON.parse(localStorage.getItem('user'));
      body.AppId = environment.ApplicationID;
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      };
      return this.httpClient.post<ResultClass>(tokenUrl, body, httpOptions).pipe(
        tap((actionInfo: ResultClass) => {
            this.StartGetting = false;
            this.logger.debug('AU2001', 'get refresh token');
            if (actionInfo && actionInfo.Success) {
              console.log('[AuthService][RefreshToken] Success');
              const data = actionInfo.Result;
              localStorage.setItem('access_token', data.token);
              delete data.AppID;
              localStorage.setItem('user', JSON.stringify(data));
            } else {
              this.toastr.error(actionInfo.Message, 'RefreshToken deny, back to login', {
                  positionClass: 'toast-top-center',
                  timeOut: 4000
                }).onHidden.subscribe(() => {
                  console.error('[AuthService][RefreshToken] res error: Refresh token failed');
                });
            }
        }, (e: any) => {
            console.error(`[AuthService][RefreshToken] error: ${e}`);
        })
      );
    }

    public getAuthToken(): string {
        console.log('[AuthService][getAuthToken]');
        const currentUser = JSON.parse(localStorage.getItem('user'));
        return currentUser != null ? currentUser.Token : '';
    }

    public logout(): void {
        console.log('[AuthService][logout]');
        localStorage.removeItem('access_token');
        localStorage.removeItem('SessionId');
        localStorage.removeItem('user');
        const token = localStorage.getItem('tk');
        const errorRedirectUri = encodeURIComponent(`${window.location.protocol}//${window.location.host}/Error`);
        const logoutAPIUri = `${environment.AuthAPIBaseUrl}auth/AADLogout?token=${token}&errorRedirectUri=${errorRedirectUri}`;
        window.location.href = logoutAPIUri;
    }

    public login(): void {
        console.log('[AuthService][login]');
        this.removeApplicationFromLocalStroage();
        const redirectUri = encodeURIComponent(window.location.href);
        const errorRedirectUri = encodeURIComponent(`${window.location.protocol}//${window.location.host}/Error`);
        const loginAPIUri = `${environment.AuthURL}?appID=${environment.ApplicationID}`;
        console.log(loginAPIUri);
        window.location.href = loginAPIUri;
    }

    public AutoLogin(successCallback: IHttpCallBack, failCallback: IHttpCallBack = null) {
      console.log('[AuthService][AutoLogin]');
      const loginUri = this.BaseUrl + 'Auth/AutoLogin';
      const httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': environment.APIBaseUrl,
            'Auth': 'auto'
        }),
        withCredentials: true
      };
      this.httpClient.post<ActionResultClass>(loginUri, null, httpOptions)
        .pipe(
            catchError(this.handleError<ActionResultClass>(`login error`))
        )
        .subscribe(res => {
            if (res && res.Status === 'Success') {
                console.log('[AuthService][AutoLogin] Success');
                const token = res.Data.Token;
                localStorage.setItem('access_token', token);
                localStorage.setItem('SessionId', res.Data.SessionId);
                localStorage.setItem('user', JSON.stringify(res.Data));
                successCallback(res);
            } else {
                const msg = res ? res.Message : 'error';
                console.error(`[AuthService][AutoLogin] res error: ${msg}`);
                if (failCallback) {
                    failCallback(res);
                }
            }
        }, err => {
           console.error(`[AuthService][AutoLogin] error: ${err}`);
        });
    }

    public CheckExpiredDate() {
      console.log('[AuthService][CheckExpiredDate]');
      const token = localStorage.getItem('access_token');
      this.isExpired = false;
      if (token) {
          const tokenInfo = this.jwtHelper.decodeToken(token);
          // let expiredDate = tokenInfo['http://schemas.microsoft.com/ws/2008/06/identity/claims/expired'];
          // let expiretion = tokenInfo['http://schemas.microsoft.com/ws/2008/06/identity/claims/expiration'];
          const expiretion = parseInt(tokenInfo.Expiration);
          const dtNow = new Date();
          const dtExpired = new Date(tokenInfo.Expired);
          const dtExpiretion = new Date(dtNow.getTime() + dtNow.getTimezoneOffset() * 60000 + (expiretion / 2) * 1000);
          if (dtExpiretion.getTime() > dtExpired.getTime()) {
              this.isExpired = true;
              return true;
          }
      }
      return false;
    }

    public removeApplicationFromLocalStroage() {
      localStorage.removeItem('access_token');
      localStorage.removeItem('SessionId');
      localStorage.removeItem('user');
    }
}
